<template>
  <div class="purchase-create-page">
    <!-- 顶部导航 -->
    <header class="back-header">
      <div class="back-btn" @click="goBack">←</div>
      <h1>{{ isEdit ? '编辑进货单' : '新建进货单' }}</h1>
    </header>

    <div class="main-content">
      <!-- 商品明细 - 移到最上面 -->
      <div class="section">
        <div class="section-header">
          <h3 class="section-title">{{ $t('wholesaler.purchase.PurchaseCreate.line13_text') }}</h3>
          <div class="action-buttons">
            <button class="tax-batch-btn" @click="openBatchTaxDialog" v-if="form.items.length > 0">批量设置税率</button>
            <button class="add-product-btn secondary" @click="goToCreateProduct">{{ $t('wholesaler.purchase.PurchaseCreate.line15_text') }}</button>
            <button class="add-product-btn" @click="openAddProduct">{{ $t('wholesaler.purchase.PurchaseCreate.line16_text') }}</button>
          </div>
        </div>

        <div class="items-table" v-if="form.items.length > 0">
          <div class="table-header">
            <span class="col-name">{{ $t('common.products') }}</span>
            <span class="col-tax">税率</span>
            <span class="col-qty">{{ $t('wholesaler.purchase.PurchaseCreate.line23_text') }}</span>
            <span class="col-price">{{ $t('wholesaler.purchase.PurchaseCreate.line24_text') }}</span>
            <span class="col-subtotal">{{ $t('wholesaler.purchase.PurchaseCreate.line25_text') }}</span>
            <span class="col-action">{{ $t('wholesaler.purchase.PurchaseCreate.line26_text') }}</span>
          </div>
          <div class="table-row" v-for="(item, index) in form.items" :key="index">
            <span class="col-name">{{ item.productName }}</span>
            <span class="col-tax">
              <span class="tax-tag" :class="getTaxClass(item)" @click="openItemTaxDialog(index)">{{ getTaxLabel(item) }}</span>
            </span>
            <span class="col-qty">
              <input type="number" v-model.number="item.quantity" @input="calcSubtotal(item)" min="0.01" step="0.01" class="mini-input" />
            </span>
            <span class="col-price">
              <input type="number" v-model.number="item.unitPrice" @input="calcSubtotal(item)" min="0" step="0.01" class="mini-input" />
            </span>
            <span class="col-subtotal">¥{{ formatMoney(item.subtotal) }}</span>
            <span class="col-action">
              <button class="delete-btn" @click="removeItem(index)">{{ $t('common.delete') }}</button>
            </span>
          </div>
          <div class="table-footer">
            <span>总计: {{ totalQuantity }}件</span>
            <span class="total-money">¥{{ formatMoney(totalAmount) }}</span>
          </div>
        </div>

        <div v-else class="empty-items">
          <p>{{ $t('wholesaler.purchase.PurchaseCreate.line48_text') }}</p>
          <button class="add-product-btn" @click="openAddProduct">{{ $t('wholesaler.purchase.PurchaseCreate.line49_text') }}</button>
        </div>
      </div>

      <!-- 基本信息 -->
      <div class="section">
        <h3 class="section-title">{{ $t('wholesaler.purchase.PurchaseCreate.line55_text') }}</h3>
        <div class="form-group">
          <label>{{ $t('wholesaler.purchase.PurchaseCreate.line57_text') }}</label>
          <div class="supplier-select">
            <select v-model="form.supplierId" @change="onSupplierChange" class="form-select">
              <option value="">{{ $t('wholesaler.purchase.PurchaseCreate.line60_text') }}</option>
              <option v-for="s in suppliers" :key="s.id" :value="s.id">{{ s.name }}</option>
            </select>
            <button class="add-supplier-btn" @click="openAddSupplier">{{ $t('wholesaler.purchase.PurchaseCreate.line63_text') }}</button>
          </div>
        </div>
        <div class="form-group">
          <label>{{ $t('wholesaler.purchase.PurchaseCreate.line67_text') }}</label>
          <select v-model="form.warehouseId" @change="onWarehouseChange" class="form-select">
            <option value="">{{ $t('wholesaler.purchase.PurchaseCreate.line69_text') }}</option>
            <option v-for="w in warehouses" :key="w.id" :value="w.id">{{ w.warehouseName }}{{ w.isDefault ? '（店铺）' : '' }}</option>
          </select>
        </div>
        <div class="form-group">
          <label>{{ $t('wholesaler.purchase.PurchaseCreate.line74_text') }}</label>
          <input type="datetime-local" v-model="form.purchaseDate" class="form-input" />
        </div>
        <div class="form-group">
          <label>{{ $t('wholesaler.purchase.PurchaseCreate.line78_text') }}</label>
          <textarea v-model="form.remark" class="form-textarea" rows="2" :placeholder="$t('wholesaler.purchase.PurchaseCreate.line79_placeholder')"></textarea>
        </div>
      </div>

      <!-- 提交按钮 -->
      <div class="submit-bar">
        <label class="auto-receive-checkbox">
          <input type="checkbox" v-model="autoReceive" />
          <span>{{ $t('wholesaler.purchase.PurchaseCreate.line87_text') }}</span>
        </label>
        <button class="btn-submit" @click="submit">{{ $t('wholesaler.purchase.PurchaseCreate.line89_text') }}</button>
      </div>
    </div>

    <!-- 添加供应商弹窗 -->
    <div v-if="showAddSupplier" class="modal-overlay modal-show" @click="showAddSupplier = false">
      <div class="modal-content" @click.stop>
        <h3>{{ $t('wholesaler.purchase.PurchaseCreate.line96_text') }}</h3>
        <div class="form-group">
          <label>{{ $t('wholesaler.purchase.PurchaseCreate.line98_text') }}</label>
          <input type="text" v-model="newSupplier.name" class="form-input" :placeholder="$t('wholesaler.purchase.PurchaseCreate.line99_placeholder')" />
        </div>
        <div class="form-group">
          <label>{{ $t('wholesaler.purchase.PurchaseCreate.line102_text') }}</label>
          <input type="text" v-model="newSupplier.contactPerson" class="form-input" />
        </div>
        <div class="form-group">
          <label>{{ $t('wholesaler.purchase.PurchaseCreate.line106_text') }}</label>
          <input type="text" v-model="newSupplier.phone" class="form-input" />
        </div>
        <div class="form-group">
          <label>{{ $t('wholesaler.purchase.PurchaseCreate.line110_text') }}</label>
          <input type="text" v-model="newSupplier.address" class="form-input" />
        </div>
        <div class="modal-actions">
          <button class="btn-cancel" @click="showAddSupplier = false">{{ $t('common.cancel') }}</button>
          <button class="btn-confirm" @click="addSupplier">{{ $t('common.confirm') }}</button>
        </div>
      </div>
    </div>

    <!-- 进货商品弹窗 -->
    <div v-if="showAddProduct" class="modal-overlay modal-show" @click="showAddProduct = false">
      <div class="modal-content modal-large" @click.stop>
        <h3>{{ $t('wholesaler.purchase.PurchaseCreate.line123_text') }}</h3>
        <div class="search-bar">
          <input type="text" v-model="productKeyword" @input="searchProducts" :placeholder="$t('wholesaler.purchase.PurchaseCreate.line125_placeholder')" class="form-input" />
        </div>
        <div class="product-list">
          <div v-for="p in productList" :key="p.id" class="product-item" @click="selectProduct(p)">
            <div class="product-info">
              <span class="product-name">{{ p.name }}</span>
              <span class="product-sku">{{ p.sku || p.code }}</span>
            </div>
            <span class="product-price">¥{{ formatMoney(p.costPrice) }}</span>
          </div>
          <div v-if="productList.length === 0" class="empty-products">{{ $t('common.noProduct') }}</div>
        </div>
        <div class="modal-actions">
          <button class="btn-cancel" @click="showAddProduct = false">{{ $t('wholesaler.purchase.PurchaseCreate.line138_text') }}</button>
        </div>
      </div>
    </div>

    <!-- 快速新建商品弹窗 -->
    <div v-if="showQuickCreateProduct" class="modal-overlay modal-show" @click="showQuickCreateProduct = false">
      <div class="modal-content" @click.stop>
        <h3>{{ $t('wholesaler.purchase.PurchaseCreate.line146_text') }}</h3>
        <div class="form-group">
          <label>{{ $t('wholesaler.purchase.PurchaseCreate.line148_text') }}</label>
          <input type="text" v-model="quickProduct.name" class="form-input" :placeholder="$t('wholesaler.purchase.PurchaseCreate.line149_placeholder')" />
        </div>
        <div class="form-group">
          <label>SKU</label>
          <input type="text" v-model="quickProduct.sku" class="form-input" :placeholder="$t('wholesaler.purchase.PurchaseCreate.line153_placeholder')" />
        </div>
        <div class="form-row">
          <div class="form-group">
            <label>{{ $t('wholesaler.purchase.PurchaseCreate.line157_text') }}</label>
            <input type="number" v-model.number="quickProduct.costPrice" class="form-input" step="0.01" min="0" placeholder="0.00" />
          </div>
          <div class="form-group">
            <label>{{ $t('wholesaler.purchase.PurchaseCreate.line161_text') }}</label>
            <input type="number" v-model.number="quickProduct.marketPrice" class="form-input" step="0.01" min="0" placeholder="0.00" />
          </div>
        </div>
        <div class="form-group">
          <label>{{ $t('wholesaler.purchase.PurchaseCreate.line166_text') }}</label>
          <input type="text" v-model="quickProduct.categoryName" class="form-input" :placeholder="$t('wholesaler.purchase.PurchaseCreate.line167_placeholder')" />
        </div>
        <div class="form-group">
          <label>{{ $t('wholesaler.purchase.PurchaseCreate.line170_text') }}</label>
          <input type="text" v-model="quickProduct.unit" class="form-input" :placeholder="$t('wholesaler.purchase.PurchaseCreate.line171_placeholder')" />
        </div>
        <div class="modal-actions">
          <button class="btn-cancel" @click="showQuickCreateProduct = false">{{ $t('common.cancel') }}</button>
          <button class="btn-confirm" @click="quickCreateProduct">{{ $t('wholesaler.purchase.PurchaseCreate.line175_text') }}</button>
        </div>
      </div>
    </div>

    <!-- 支付弹窗 -->
    <div v-if="showPaymentDialog" class="modal-overlay modal-show" @click="skipPayment">
      <div class="modal-content" @click.stop>
        <h3>支付 - {{ submittedOrder.purchaseNo }}</h3>
        <div class="form-group">
          <label>{{ $t('wholesaler.purchase.PurchaseCreate.line185_text') }}</label>
          <div class="money-display">¥{{ formatMoney(paymentForm.amount) }}</div>
        </div>
        <div class="form-group">
          <label>{{ $t('wholesaler.purchase.PurchaseCreate.line189_text') }}</label>
          <input type="number" v-model="paymentForm.amount" step="0.01" min="0.01" class="form-input" @blur="roundAmount" />
        </div>
        <div class="form-group">
          <label>{{ $t('wholesaler.purchase.PurchaseCreate.line193_text') }}</label>
          <select v-model="paymentForm.method" class="form-select">
            <option value="CASH">{{ $t('wholesaler.purchase.PurchaseCreate.line195_text') }}</option>
            <option value="CREDIT_CARD">{{ $t('wholesaler.purchase.PurchaseCreate.line196_text') }}</option>
            <option value="BANK_TRANSFER">{{ $t('wholesaler.purchase.PurchaseCreate.line197_text') }}</option>
            <option value="ONLINE_BANKING">{{ $t('wholesaler.purchase.PurchaseCreate.line198_text') }}</option>
          </select>
        </div>
        <div class="form-group">
          <label>{{ $t('wholesaler.purchase.PurchaseCreate.line202_text') }}</label>
          <input type="file" @change="uploadProof" accept="image/*" class="form-input" />
          <img v-if="paymentForm.proofUrl" :src="getImageUrl(paymentForm.proofUrl)" class="proof-preview" />
        </div>
        <div class="form-group">
          <label>{{ $t('wholesaler.purchase.PurchaseCreate.line207_text') }}</label>
          <textarea v-model="paymentForm.remark" class="form-textarea" rows="2"></textarea>
        </div>
        <div class="modal-actions">
          <button class="btn-cancel" @click="skipPayment">{{ $t('wholesaler.purchase.PurchaseCreate.line211_text') }}</button>
          <button class="btn-confirm" @click="submitPayment">{{ $t('wholesaler.purchase.PurchaseCreate.line212_text') }}</button>
        </div>
      </div>
    </div>

    <!-- 批量设置税率弹窗 -->
    <div v-if="showBatchTaxDialog" class="modal-overlay modal-show" @click="showBatchTaxDialog = false">
      <div class="modal-content" @click.stop>
        <h3>批量设置税率</h3>
        <div class="form-group">
          <label>税率类型</label>
          <select v-model="batchTaxForm.taxType" class="form-select" @change="onTaxTypeChange">
            <option value="NONE">无税</option>
            <option value="INCLUSIVE">含税（价格已包含税）</option>
            <option value="EXCLUSIVE">额外加税（价格需另加税）</option>
          </select>
        </div>
        <div class="form-group" v-if="batchTaxForm.taxType !== 'NONE'">
          <label>税率</label>
          <select v-model="batchTaxForm.taxRate" class="form-select" @change="applyBatchTax">
            <option v-for="config in taxRateConfigs" :key="config.id" :value="config.rateValue">
              {{ config.rateName }}
            </option>
            <option v-if="taxRateConfigs.length === 0" disabled value="">暂无税率配置</option>
          </select>
        </div>
        <div class="modal-actions">
          <button class="btn-cancel" @click="showBatchTaxDialog = false">取消</button>
          <button class="btn-confirm" @click="applyBatchTax">应用到所有商品</button>
        </div>
      </div>
    </div>

    <!-- 单个商品税率设置弹窗 -->
    <div v-if="showItemTaxDialog" class="modal-overlay modal-show" @click="showItemTaxDialog = false">
      <div class="modal-content" @click.stop>
        <h3>设置税率 - {{ currentItem && currentItem.productName }}</h3>
        <div class="form-group">
          <label>税率类型</label>
          <select v-model="currentItem.taxType" class="form-select" @change="onItemTaxTypeChange">
            <option value="NONE">无税</option>
            <option value="INCLUSIVE">含税（价格已包含税）</option>
            <option value="EXCLUSIVE">额外加税（价格需另加税）</option>
          </select>
        </div>
        <div class="form-group" v-if="currentItem && currentItem.taxType !== 'NONE'">
          <label>税率</label>
          <select v-model="currentItem.taxRate" class="form-select" @change="closeItemTaxDialog">
            <option v-for="config in taxRateConfigs" :key="config.id" :value="config.rateValue">
              {{ config.rateName }}
            </option>
            <option v-if="taxRateConfigs.length === 0" disabled value="">暂无税率配置</option>
          </select>
        </div>
        <div class="modal-actions">
          <button class="btn-cancel" @click="showItemTaxDialog = false">取消</button>
          <button class="btn-confirm" @click="closeItemTaxDialog">确认</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getImageUrl } from '@/utils/image';
import {
  createPurchaseOrder,
  updatePurchaseOrder,
  getPurchaseOrderDetail,
  getActiveSuppliers,
  createSupplier,
  getProductList,
  getWarehouseList,
  payPurchaseOrder
} from '@/api/purchase'
import { getWholesalerId } from '@/utils/wholesaler'
import { getActiveTaxRateConfigs } from '@/api/purchase'

export default {
  name: 'PurchaseCreate',

  data() {
    return {
      isEdit: false,
      purchaseId: null,
      form: {
        supplierId: '',
        supplierName: '',
        warehouseId: '',
        warehouseName: '',
        purchaseDate: new Date().toISOString().slice(0, 16),
        remark: '',
        items: []
      },
      suppliers: [],
      warehouses: [],
      autoReceive: true,  // 默认自动收货
      showAddSupplier: false,
      newSupplier: {
        name: '',
        contactPerson: '',
        phone: '',
        address: ''
      },
      showAddProduct: false,
      productKeyword: '',
      productList: [],
      showQuickCreateProduct: false,
      quickProduct: {
        name: '',
        sku: '',
        costPrice: 0,
        marketPrice: 0,
        categoryName: '',
        unit: ''
      },
      showPaymentDialog: false,
      submittedOrder: null,
      paymentForm: {
        amount: 0,
        method: 'CASH',
        remark: '',
        proofUrl: ''
      },
      // 税率相关
      taxRateConfigs: [], // 从后台加载的税率配置
      showBatchTaxDialog: false,
      batchTaxForm: {
        taxType: 'NONE',
        taxRate: 0.09
      },
      showItemTaxDialog: false,
      currentItemIndex: null,
      currentItem: null
    }
  },

  computed: {
    wholesalerId() {
      return getWholesalerId()
    },
    totalQuantity() {
      return this.form.items.reduce((sum, item) => sum + (item.quantity || 0), 0)
    },
    totalAmount() {
      return this.form.items.reduce((sum, item) => sum + (item.subtotal || 0), 0)
    }
  },

  created() {
    // 检查批发商 ID
    if (!this.wholesalerId) {
      console.error('❌ 未找到批发商 ID，请确认已登录批发商账号')
      if (this.$toast) {
        this.$toast.error('未找到批发商身份，请重新登录')
      }
      return
    }
    console.log('✅ 批发商 ID:', this.wholesalerId)
    
    this.loadSuppliers()
    this.loadWarehouses()
    this.loadTaxRateConfigs() // 加载税率配置
    
    // 检查是否是编辑模式
    const id = this.$route.params.id
    if (id) {
      this.isEdit = true
      this.purchaseId = id
      this.loadDetail(id)
    }
  },

  methods: {
    getImageUrl,
    async loadSuppliers() {
      try {
        console.log('📦 加载供应商, wholesalerId:', this.wholesalerId)
        const res = await getActiveSuppliers(this.wholesalerId)
        this.suppliers = res || []
        console.log('✅ 供应商加载成功:', this.suppliers.length, '个')
      } catch (e) {
        console.error('❌ 加载供应商失败', e)
        if (this.$toast) {
          this.$toast.error('加载供应商失败: ' + e.message)
        }
      }
    },

    async loadWarehouses() {
      try {
        console.log('📦 加载仓库, wholesalerId:', this.wholesalerId)
        const res = await getWarehouseList(this.wholesalerId)
        const warehouseList = res || []
        console.log('✅ 仓库加载成功:', warehouseList.length, '个')
        
        // 检查是否有店铺仓库（isDefault=true）
        const hasStore = warehouseList.some(w => w.isDefault === true)
        if (!hasStore) {
          // 如果没有店铺仓库，添加一个虚拟的店铺选项
          this.warehouses = [
            { id: null, warehouseName: '店铺（默认）', isDefault: true },
            ...warehouseList
          ]
        } else {
          this.warehouses = warehouseList
        }
        console.log('📋 仓库列表:', this.warehouses)
      } catch (e) {
        console.error('❌ 加载仓库失败', e)
        if (this.$toast) {
          this.$toast.error('加载仓库失败: ' + e.message)
        }
      }
    },

    async loadTaxRateConfigs() {
      try {
        console.log('📊 加载税率配置, wholesalerId:', this.wholesalerId)
        const res = await getActiveTaxRateConfigs(this.wholesalerId)
        this.taxRateConfigs = res || []
        console.log('✅ 税率配置加载成功:', this.taxRateConfigs.length, '个')
        
        // 如果有配置，设置默认税率为第一个配置
        if (this.taxRateConfigs.length > 0) {
          this.batchTaxForm.taxRate = this.taxRateConfigs[0].rateValue
        }
      } catch (e) {
        console.error('❌ 加载税率配置失败', e)
        // 税率配置加载失败不影响其他功能
        this.taxRateConfigs = []
      }
    },

    async loadDetail(id) {
      try {
        const data = await getPurchaseOrderDetail(id, this.wholesalerId)
        this.form.supplierId = data.supplierId
        this.form.supplierName = data.supplierName
        this.form.warehouseId = data.warehouseId
        this.form.warehouseName = data.warehouseName
        this.form.purchaseDate = data.purchaseDate
        this.form.remark = data.remark
        this.form.items = data.items || []
      } catch (e) {
        console.error('加载详情失败', e)
        this.$toast.error('加载失败')
      }
    },

    onSupplierChange() {
      const supplier = this.suppliers.find(s => s.id === this.form.supplierId)
      this.form.supplierName = supplier ? supplier.name : ''
      console.log('选择供应商:', this.form.supplierName)
    },

    onWarehouseChange() {
      const warehouse = this.warehouses.find(w => w.id === this.form.warehouseId)
      this.form.warehouseName = warehouse ? warehouse.warehouseName : ''
      console.log('选择仓库:', this.form.warehouseName)
    },

    openAddSupplier() {
      console.log('🔔 打开供应商新增弹窗')
      if (!this.wholesalerId) {
        if (this.$toast) {
          this.$toast.error('批发商 ID 未初始化，无法添加供应商')
        }
        return
      }
      this.showAddSupplier = true
    },

    openAddProduct() {
      console.log('🔔 打开商品添加弹窗')
      if (!this.wholesalerId) {
        if (this.$toast) {
          this.$toast.error('批发商 ID 未初始化，无法进货商品')
        }
        return
      }
      this.showAddProduct = true
      // 加载商品列表
      this.searchProducts()
    },

    openQuickCreateProduct() {
      console.log('🔔 打开快速新建商品弹窗')
      if (!this.wholesalerId) {
        if (this.$toast) {
          this.$toast.error('批发商 ID 未初始化，无法创建商品')
        }
        return
      }
      this.quickProduct = {
        name: '',
        sku: '',
        costPrice: 0,
        marketPrice: 0,
        categoryName: '',
        unit: ''
      }
      this.showQuickCreateProduct = true
    },

    goToCreateProduct() {
      console.log('🔔 跳转到新建商品页面')
      this.$router.push('/wholesaler/product/create')
    },

    async quickCreateProduct() {
      if (!this.quickProduct.name.trim()) {
        this.$toast.error('请输入商品名称')
        return
      }
      if (this.quickProduct.costPrice <= 0) {
        this.$toast.error('成本价必须大于 0')
        return
      }
      if (this.quickProduct.marketPrice <= 0) {
        this.$toast.error('销售价必须大于 0')
        return
      }

      try {
        console.log('📦 快速创建商品:', this.quickProduct)
        const { createProduct } = require('@/api/wholesaler/product')
        const productData = {
          name: this.quickProduct.name,
          sku: this.quickProduct.sku,
          costPrice: this.quickProduct.costPrice,
          marketPrice: this.quickProduct.marketPrice,
          price: this.quickProduct.marketPrice,
          categoryName: this.quickProduct.categoryName,
          unit: this.quickProduct.unit || '件',
          wholesalerId: this.wholesalerId
        }
        const res = await createProduct(productData)
        console.log('✅ 商品创建成功:', res)
        this.$toast.success('商品创建成功')
        this.showQuickCreateProduct = false
        
        // 自动添加到商品列表
        this.form.items.push({
          productId: res.id,
          productName: res.name,
          productSku: res.sku,
          quantity: 1,
          unit: res.unit,
          unitPrice: res.costPrice,
          subtotal: res.costPrice,
          remark: ''
        })
      } catch (e) {
        console.error('❌ 创建商品失败', e)
        this.$toast.error('创建失败: ' + e.message)
      }
    },

    async addSupplier() {
      if (!this.newSupplier.name.trim()) {
        this.$toast.error('请输入供应商名称')
        return
      }
      try {
        console.log('📦 添加供应商:', this.newSupplier)
        const res = await createSupplier({
          ...this.newSupplier,
          wholesalerId: this.wholesalerId,
          status: 'ACTIVE'
        })
        console.log('✅ 供应商添加成功:', res)
        this.$toast.success('供应商添加成功')
        this.showAddSupplier = false
        this.newSupplier = { name: '', contactPerson: '', phone: '', address: '' }
        await this.loadSuppliers()
        // 自动选中新添加的供应商
        this.form.supplierId = res.id
        this.onSupplierChange()
      } catch (e) {
        console.error('❌ 添加供应商失败', e)
        this.$toast.error('添加失败: ' + e.message)
      }
    },

    async searchProducts() {
      try {
        console.log('🔍 搜索商品, keyword:', this.productKeyword)
        const res = await getProductList(this.wholesalerId, this.productKeyword)
        this.productList = res?.content || res || []
        console.log('✅ 商品搜索成功:', this.productList.length, '个')
      } catch (e) {
        console.error('❌ 搜索商品失败', e)
        if (this.$toast) {
          this.$toast.error('搜索商品失败: ' + e.message)
        }
      }
    },

    selectProduct(product) {
      // 检查是否已添加
      if (this.form.items.find(i => i.productId === product.id)) {
        this.$toast.warning('该商品已添加')
        return
      }
      this.form.items.push({
        productId: product.id,
        productName: product.name,
        productSku: product.sku || product.code,
        quantity: 1,
        unit: product.unit || '件',
        unitPrice: product.costPrice || 0,
        subtotal: product.costPrice || 0,
        taxType: 'NONE',
        taxRate: 0,
        remark: ''
      })
      this.showAddProduct = false
      this.productKeyword = ''
    },

    calcSubtotal(item) {
      const base = (item.quantity || 0) * (item.unitPrice || 0)
      if (item.taxType === 'EXCLUSIVE' && item.taxRate > 0) {
        // 额外加税：小计 = 基础金额 × (1 + 税率)
        item.subtotal = base * (1 + item.taxRate)
      } else {
        // 无税或含税：小计 = 基础金额
        item.subtotal = base
      }
    },

    removeItem(index) {
      this.form.items.splice(index, 1)
    },

    async submit() {
      // 验证
      // warehouseId 为 null 表示店铺仓库，也是合法值
      if (this.form.warehouseId === '' || this.form.warehouseId === undefined) {
        this.$toast.error('请选择入库仓库')
        return
      }
      if (this.form.items.length === 0) {
        this.$toast.error('请进货商品')
        return
      }

      try {
        const submitData = {
          ...this.form,
          // warehouseId 为空字符串或 null 都表示店铺仓库，统一转为 null
          warehouseId: (!this.form.warehouseId || this.form.warehouseId === '') ? null : this.form.warehouseId,
          items: this.form.items.map(item => ({
            productId: item.productId,
            productName: item.productName,
            productSku: item.productSku,
            quantity: item.quantity,
            unit: item.unit,
            unitPrice: item.unitPrice,
            taxType: item.taxType || 'NONE',
            taxRate: item.taxRate || 0,
            remark: item.remark
          }))
        }

        let res
        if (this.isEdit) {
          res = await updatePurchaseOrder(this.purchaseId, submitData, this.wholesalerId)
        } else {
          res = await createPurchaseOrder(submitData, this.wholesalerId)
        }

        const orderId = res.id
        
        // 确认进货单
        const { confirmPurchaseOrder } = require('@/api/purchase')
        await confirmPurchaseOrder(orderId, this.wholesalerId)
        
        // 如果勾选了自动收货
        if (this.autoReceive) {
          const { receivePurchaseOrder } = require('@/api/purchase')
          const userId = localStorage.getItem('user_id')
          await receivePurchaseOrder(orderId, this.wholesalerId, userId)
          this.$toast.success('进货单已提交并收货入库')
        } else {
          this.$toast.success('进货单已确认，待收货')
        }

        // 检查是否需要支付
        const unpaidAmount = res.totalAmount - (res.paidAmount || 0)
        if (res.paymentStatus !== 'PAID' && unpaidAmount > 0) {
          // 弹出支付弹窗
          this.submittedOrder = res
          this.paymentForm.amount = Math.round(unpaidAmount * 100) / 100
          this.paymentForm.method = 'CASH'
          this.paymentForm.remark = ''
          this.paymentForm.proofUrl = ''
          this.showPaymentDialog = true
        } else {
          this.$router.push('/wholesaler/purchase-list')
        }
      } catch (e) {
        console.error('提交失败', e)
        this.$toast.error('操作失败: ' + e.message)
      }
    },

    goBack() {
      this.$router.go(-1)
    },

    formatMoney(value) {
      if (!value) return '0.00'
      return Number(value).toFixed(2)
    },

    async submitPayment() {
      if (!this.paymentForm.amount || this.paymentForm.amount <= 0) {
        this.$toast.error('请输入支付金额')
        return
      }
      if (!this.paymentForm.proofUrl) {
        this.$toast.error('请上传支付凭证')
        return
      }
      try {
        const operatorId = localStorage.getItem('user_id')
        await payPurchaseOrder(this.submittedOrder.id, {
          amount: this.paymentForm.amount,
          paymentMethod: this.paymentForm.method,
          remark: this.paymentForm.remark,
          proofUrl: this.paymentForm.proofUrl
        }, this.wholesalerId, operatorId)
        this.$toast.success('支付成功')
        this.showPaymentDialog = false
        this.$router.push('/wholesaler/purchase-list')
      } catch (e) {
        console.error('支付失败', e)
        this.$toast.error('支付失败: ' + (e.response?.data?.message || e.message))
      }
    },

    async uploadProof(e) {
      const file = e.target.files[0]
      if (!file) return
      try {
        const { uploadFile } = require('@/services/api')
        const res = await uploadFile('/files/upload-image', file)
        this.paymentForm.proofUrl = res.url
        this.$toast.success('凭证上传成功')
      } catch (err) {
        console.error('上传失败', err)
        this.$toast.error('上传失败: ' + err.message)
      }
    },

    skipPayment() {
      this.showPaymentDialog = false
      this.$router.push('/wholesaler/purchase-list')
    },
    roundAmount() {
      if (this.paymentForm.amount) {
        this.paymentForm.amount = Math.round(this.paymentForm.amount * 100) / 100
      }
    },
    
    // 税率相关方法
    getTaxLabel(item) {
      if (!item.taxType || item.taxType === 'NONE') {
        return '无税'
      }
      const rate = Math.round((item.taxRate || 0) * 100)
      if (item.taxType === 'INCLUSIVE') {
        return `含税${rate}%`
      } else if (item.taxType === 'EXCLUSIVE') {
        return `+税${rate}%`
      }
      return '无税'
    },
    
    getTaxClass(item) {
      if (!item.taxType || item.taxType === 'NONE') {
        return 'tax-none'
      }
      if (item.taxType === 'INCLUSIVE') {
        return 'tax-inclusive'
      } else if (item.taxType === 'EXCLUSIVE') {
        return 'tax-exclusive'
      }
      return ''
    },
    
    openBatchTaxDialog() {
      this.showBatchTaxDialog = true
    },
    
    onTaxTypeChange() {
      if (this.batchTaxForm.taxType === 'NONE') {
        // 选择无税，直接应用并关闭
        this.applyBatchTax()
      }
      // 其他情况等待用户选择税率
    },
    
    applyBatchTax() {
      if (this.batchTaxForm.taxType === 'NONE') {
        // 无税，清空所有商品的税率
        this.form.items.forEach(item => {
          item.taxType = 'NONE'
          item.taxRate = 0
          this.calcSubtotal(item)
        })
      } else {
        // 设置税率
        this.form.items.forEach(item => {
          item.taxType = this.batchTaxForm.taxType
          item.taxRate = this.batchTaxForm.taxRate
          this.calcSubtotal(item)
        })
      }
      this.showBatchTaxDialog = false
      this.$toast.success('已批量设置税率')
    },
    
    openItemTaxDialog(index) {
      this.currentItemIndex = index
      this.currentItem = this.form.items[index]
      // 确保有默认值
      if (!this.currentItem.taxType) {
        this.currentItem.taxType = 'NONE'
      }
      if (!this.currentItem.taxRate) {
        this.currentItem.taxRate = 0.09
      }
      this.showItemTaxDialog = true
    },
    
    onItemTaxTypeChange() {
      if (this.currentItem && this.currentItem.taxType === 'NONE') {
        // 选择无税，设置税率为0
        this.currentItem.taxRate = 0
      } else if (this.currentItem && this.currentItem.taxType !== 'NONE') {
        // 选择含税或额外加税，如果没有税率，设置默认值
        if (!this.currentItem.taxRate || this.currentItem.taxRate === 0) {
          if (this.taxRateConfigs && this.taxRateConfigs.length > 0) {
            this.currentItem.taxRate = this.taxRateConfigs[0].rateValue
          } else {
            this.currentItem.taxRate = 0.09
          }
        }
      }
    },
    
    closeItemTaxDialog() {
      this.showItemTaxDialog = false
      if (this.currentItem) {
        this.calcSubtotal(this.currentItem)
      }
      this.$toast.success('已设置税率')
    }
  }
}
</script>

<style scoped>
.purchase-create-page {
  min-height: 100vh;
  background: #f5f5f5;
  padding-bottom: 80px;
}

.back-header {
  display: flex;
  align-items: center;
  padding: calc(12px + env(safe-area-inset-top, 0px)) 16px;
  background: white;
  border-bottom: 1px solid #eee;
  position: sticky;
  top: 0;
  z-index: 100;
}

.back-btn {
  font-size: 20px;
  cursor: pointer;
  padding: 4px 8px;
}

.back-header h1 {
  flex: 1;
  font-size: 18px;
  margin: 0;
}

.main-content {
  padding: 16px;
}

.section {
  background: white;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.section-title {
  font-size: 16px;
  font-weight: bold;
  margin: 0 0 12px 0;
}

.form-group {
  margin-bottom: 16px;
}

.form-group label {
  display: block;
  margin-bottom: 6px;
  font-size: 14px;
  color: #666;
}

.form-input, .form-select, .form-textarea {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  box-sizing: border-box;
}

.supplier-select {
  display: flex;
  gap: 8px;
}

.supplier-select select {
  flex: 1;
}

.add-supplier-btn {
  padding: 10px 16px;
  background: #1976d2;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  white-space: nowrap;
}

.add-product-btn {
  padding: 8px 16px;
  background: #ff6600;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.items-table {
  border: 1px solid #eee;
  border-radius: 4px;
  overflow: hidden;
}

.table-header {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr 1fr 60px;
  background: #f5f5f5;
  padding: 10px;
  font-size: 13px;
  color: #666;
}

.table-row {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr 1fr 60px;
  padding: 10px;
  border-top: 1px solid #eee;
  align-items: center;
  font-size: 14px;
}

.mini-input {
  width: 100%;
  padding: 6px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 13px;
  box-sizing: border-box;
}

.col-subtotal {
  color: #ff6600;
  font-weight: bold;
}

.delete-btn {
  padding: 4px 8px;
  background: none;
  border: 1px solid #f44336;
  color: #f44336;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
}

.table-footer {
  display: flex;
  justify-content: space-between;
  padding: 12px;
  background: #fafafa;
  border-top: 1px solid #eee;
  font-weight: bold;
}

.total-money {
  color: #ff6600;
  font-size: 16px;
}

.empty-items {
  text-align: center;
  padding: 40px;
  color: #999;
}

.empty-items button {
  margin-top: 12px;
}

.action-buttons {
  display: flex;
  gap: 8px;
}

.add-product-btn.secondary {
  background: #fff;
  color: #ff6600;
  border: 1px solid #ff6600;
}

.add-product-btn.secondary:hover {
  background: #fff5f0;
}

.form-row {
  display: flex;
  gap: 12px;
}

.form-row .form-group {
  flex: 1;
}

.submit-bar {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  gap: 12px;
  padding: 16px;
  background: white;
  border-top: 1px solid #eee;
}

.btn-submit {
  flex: 1;
  padding: 12px;
  border: none;
  border-radius: 4px;
  font-size: 16px;
  cursor: pointer;
  background: #ff6600;
  color: white;
}

.auto-receive-checkbox {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  color: #666;
  cursor: pointer;
  white-space: nowrap;
}

.auto-receive-checkbox input {
  width: 18px;
  height: 18px;
  cursor: pointer;
}

/* Modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  border-radius: 8px;
  padding: 24px;
  width: 90%;
  max-width: 400px;
}

.modal-content.modal-large {
  max-width: 500px;
  max-height: 80vh;
  overflow-y: auto;
}

.modal-content h3 {
  margin: 0 0 16px 0;
}

.search-bar {
  margin-bottom: 12px;
}

.product-list {
  max-height: 400px;
  overflow-y: auto;
}

.product-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  border-bottom: 1px solid #eee;
  cursor: pointer;
}

.product-item:hover {
  background: #f5f5f5;
}

.product-info {
  display: flex;
  flex-direction: column;
}

.product-name {
  font-size: 14px;
}

.product-sku {
  font-size: 12px;
  color: #999;
}

.product-price {
  color: #ff6600;
  font-weight: bold;
}

.empty-products {
  text-align: center;
  padding: 40px;
  color: #999;
}

.modal-actions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 20px;
}

.btn-cancel, .btn-confirm {
  padding: 8px 20px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}

.btn-cancel {
  background: white;
  border: 1px solid #ddd;
}

.btn-confirm {
  background: #ff6600;
  color: white;
  border: none;
}

.money-display {
  font-size: 24px;
  font-weight: bold;
  color: #ff6600;
  margin: 8px 0;
}

.form-group {
  margin-bottom: 16px;
}

.form-group label {
  display: block;
  margin-bottom: 4px;
  font-size: 14px;
  color: #666;
}

.form-input, .form-select, .form-textarea {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  box-sizing: border-box;
}

.proof-preview {
  max-width: 100%;
  max-height: 200px;
  margin-top: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
}

/* 税率相关样式 */
.tax-batch-btn {
  padding: 8px 16px;
  background: #4CAF50;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}

.tax-batch-btn:hover {
  background: #45a049;
}

.col-tax {
  text-align: center;
}

.tax-tag {
  display: inline-block;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.3s;
}

.tax-tag:hover {
  opacity: 0.8;
  transform: scale(1.05);
}

.tax-none {
  background: #fff;
  color: #1890ff;
  border: 1px solid #1890ff;
}

.tax-inclusive {
  background: #2196F3;
  color: white;
}

.tax-exclusive {
  background: #FF9800;
  color: white;
}

.table-header .col-tax {
  font-weight: bold;
}
</style>
